import React, { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import { DateRange } from "../types/PerformanceTypes";
import { fmtFull, todayStr } from "../utils/headerUtils";

const QUICK_RANGES = [
  { label: "7j",   days: 7   },
  { label: "30j",  days: 30  },
  { label: "90j",  days: 90  },
  { label: "1 an", days: 365 },
];

interface Props {
  dateRange:    DateRange;
  activePeriod: number | null;
  onQuickRange: (days: number) => void;
  onDateChange: (r: DateRange) => void;
}

const PeriodSelector: React.FC<Props> = ({
  dateRange, activePeriod, onQuickRange, onDateChange,
}) => {
  const [open,        setOpen]        = useState(false);
  const [customStart, setCustomStart] = useState(dateRange.startDate);
  const [customEnd,   setCustomEnd]   = useState(dateRange.endDate);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCustomStart(dateRange.startDate);
    setCustomEnd(dateRange.endDate);
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const handleApply = () => {
    if (!customStart || !customEnd || customStart > customEnd) return;
    onDateChange({ startDate: customStart, endDate: customEnd });
    setOpen(false);
  };

  const triggerLabel = activePeriod
    ? `${activePeriod}j`
    : `${fmtFull(dateRange.startDate)} – ${fmtFull(dateRange.endDate)}`;

  const invalid = !!customStart && !!customEnd && customStart > customEnd;

  return (
    <div className="relative" ref={ref}>

      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
          font-semibold border transition-all duration-150 whitespace-nowrap
          ${open
            ? "bg-[#0F52BA] text-white border-[#0F52BA] shadow-sm"
            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
          }`}
      >
        <Calendar size={12} />
        {triggerLabel}
        <ChevronDown
          size={11}
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full mt-2 z-[200] w-72 bg-white
          rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/60
          animate-[fadeIn_0.15s_ease]">

          {/* Quick pills */}
          <div className="px-4 pt-4 pb-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
              Période rapide
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {QUICK_RANGES.map(({ label, days }) => (
                <button
                  key={days}
                  onClick={() => { onQuickRange(days); setOpen(false); }}
                  className={`py-1.5 rounded-lg text-[11px] font-semibold border transition-all
                    ${activePeriod === days
                      ? "bg-[#0F52BA] text-white border-[#0F52BA] shadow-sm"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mx-4 border-t border-slate-100" />

          {/* Custom range */}
          <div className="px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
              Période personnalisée
            </p>

            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Du
                </label>
                <input
                  type="date"
                  value={customStart}
                  max={customEnd || todayStr()}
                  onChange={e => setCustomStart(e.target.value)}
                  className="px-2.5 py-1.5 rounded-lg text-[12px] font-mono text-slate-700
                    border border-slate-200 bg-white focus:outline-none focus:border-blue-400
                    focus:ring-2 focus:ring-blue-100 transition-all w-full"
                />
              </div>

              <span className="text-slate-300 pb-1.5 flex-shrink-0">→</span>

              <div className="flex flex-col gap-1 flex-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Au
                </label>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart}
                  max={todayStr()}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="px-2.5 py-1.5 rounded-lg text-[12px] font-mono text-slate-700
                    border border-slate-200 bg-white focus:outline-none focus:border-blue-400
                    focus:ring-2 focus:ring-blue-100 transition-all w-full"
                />
              </div>
            </div>

            {invalid && (
              <p className="mt-1.5 text-[10px] text-rose-500">
                La date de fin doit être après le début
              </p>
            )}

            <button
              onClick={handleApply}
              disabled={invalid || !customStart || !customEnd}
              className="mt-3 w-full py-2 rounded-xl text-[12px] font-semibold
                bg-[#0F52BA] text-white hover:bg-blue-700 transition-colors
                disabled:opacity-40 disabled:cursor-not-allowed shadow-sm shadow-blue-200"
            >
              Appliquer
            </button>
          </div>

          {/* Active range badge */}
          {!activePeriod && (
            <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-[10px] text-blue-600 font-medium">
                Période active : {fmtFull(dateRange.startDate)} → {fmtFull(dateRange.endDate)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;